import { Injectable } from '@angular/core';

/**
 * Utility service for managing agent_clients flag in LocalStorage.
 *
 * Initialization logic is handled by AgentClientsGuard (global guard).
 * This service provides only utility methods for reading/writing the agent_clients array.
 */
@Injectable({
  providedIn: 'root',
})
export class AgentClientsService {
  private storageKey = 'agent_clients';

  constructor() {}

  /**
   * Ensures localStorage.agent_clients exists, initializing as [] if missing.
   */
  ensureLocalStorage(): void {
    try {
      const val = localStorage.getItem(this.storageKey);
      if (!val) {
        localStorage.setItem(this.storageKey, JSON.stringify([]));
      }
    } catch (e) {
      console.warn('[AgentClientsService] localStorage not available', e);
    }
  }

  /**
   * Returns the array of agent client user IDs from localStorage.
   * @returns Array of user IDs, or empty array if not found/invalid
   */
  getAgentClients(): string[] {
    try {
      const raw = localStorage.getItem(this.storageKey) || '[]';
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return arr;
    } catch (e) {
      console.warn('[AgentClientsService] error reading agent_clients', e);
    }
    return [];
  }

  /**
   * Adds a user ID to the agent_clients array (avoids duplicates).
   * @param id User ID to add
   */
  addAgentClient(id: string): void {
    if (!id) return;
    try {
      const arr = this.getAgentClients();
      if (!arr.includes(id)) {
        arr.push(id);
        localStorage.setItem(this.storageKey, JSON.stringify(arr));
      }
    } catch (e) {
      console.warn('[AgentClientsService] error adding agent client', e);
    }
  }

  /**
   * Checks if a given user ID is present in agent_clients array.
   * @param userId User ID to check
   * @returns true if user is an agent client, false otherwise
   */
  isAgentClient(userId?: string | null): boolean {
    if (!userId) return false;
    const arr = this.getAgentClients();
    return arr.includes(userId);
  }

  /**
   * Checks if the current session is an agent impersonation by looking
   * for the `impersonated_by` claim in the Supabase JWT.
   */
  isAgentSession(): boolean {
    try {
      const token = this.getAccessTokenFromStorage();
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return !!payload.impersonated_by;
    } catch {
      return false;
    }
  }

  isSimpleQuestionnaireSession(): boolean {
    return this.isAgentSession() && localStorage.getItem('simpleQuestionnaireSession') === 'true';
  }

  /**
   * CPK-4845: the agent opened this questionnaire through the "Arquivos Opcionais"
   * button — the one-time-link keeps its id in agentUploadQuestionnaireId (skipGuide
   * mode) until the guide finishes it. Only that exact questionnaire counts.
   */
  isAgentFileUploadSessionFor(questionnaireId: string | null | undefined): boolean {
    if (!questionnaireId) return false;
    return this.isAgentSession() && localStorage.getItem('agentUploadQuestionnaireId') === questionnaireId;
  }

  /**
   * Returns the agent's cp_user.id from the impersonated_by JWT claim.
   */
  getImpersonatingAgentId(): string | null {
    try {
      const token = this.getAccessTokenFromStorage();
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.impersonated_by || null;
    } catch {
      return null;
    }
  }

  /**
   * Extracts the Supabase access_token from localStorage.
   * Supabase stores auth under a key matching `sb-*-auth-token`.
   */
  private getAccessTokenFromStorage(): string | null {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            return parsed.access_token || null;
          }
        }
      }
    } catch {
      // ignore parse errors
    }
    return null;
  }
}
